/**
 * Ægis — Main entry point
 *
 * Wires the pipeline (perception → inference → policy) to the UI.
 *
 * "I may be mistaken." — a.Weil
 */

import './styles/aegis.css';
import { AegisPipeline } from './pipeline/aegis-pipeline';
import { AppUI } from './ui/app-ui';

// ---------------------------------------------------------------------------
// Initialize
// ---------------------------------------------------------------------------

const pipeline = new AegisPipeline({
  onConditionUpdate: (state) => {
    ui.updateCondition(state);
  },
  onIntervention: (intervention) => {
    ui.showIntervention(intervention);
  },
  onTierChange: (tier) => {
    ui.updateTier(tier);
  },
  onPerceptionStateChange: (state) => {
    ui.updatePerceptionState(state);
  },
  onPolicyReasoning: (reasoning) => {
    ui.updatePolicyReasoning(reasoning);
  },
  onRoomPresenceChange: (presence) => {
    ui.updateRoomPresence(presence);
  },
});

const ui = new AppUI(pipeline);

// Start on the home screen
ui.showScreen('home');

// Log startup
console.info(
  '%c◉ Ægis %c— instrument for attentive learning',
  'color: #1a1a1a; font-weight: bold; font-size: 14px;',
  'color: #9a9590; font-size: 14px;',
);
console.info(
  '%c"Attention, taken to its highest degree, is the same thing as generosity."',
  'color: #9a9590; font-style: italic;',
);
console.info(
  '%c◉ SAMWISE %c— companion ready (voice: en-GB-OliverNeural)',
  'color: #c4917a; font-weight: bold; font-size: 12px;',
  'color: #9a9590; font-size: 12px;',
);

// ---------------------------------------------------------------------------
// Register Service Worker (PWA offline support)
// ---------------------------------------------------------------------------

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(
      (reg) => console.info('◉ Ægis SW registered:', reg.scope),
      (err) => console.warn('◉ Ægis SW registration failed:', err),
    );
  });
}

// ---------------------------------------------------------------------------
// Fullscreen + landscape lock for kiosk-like PWA experience
// ---------------------------------------------------------------------------

/** Detect if running as installed PWA (standalone) */
function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as any).standalone === true;
}

/**
 * Enter fullscreen and lock to landscape.
 * Must be called from a user gesture (click/touch).
 * In standalone PWA mode on Fire tablets, this gives a
 * near-kiosk experience (status bar hidden, no browser chrome).
 */
async function enterKioskMode(): Promise<void> {
  try {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      await el.requestFullscreen({ navigationUI: 'hide' });
    }
  } catch {
    // Fullscreen denied — fine, app still works without it
  }

  try {
    if (screen.orientation && 'lock' in screen.orientation) {
      await (screen.orientation as any).lock('landscape');
    }
  } catch {
    // Orientation lock not supported outside fullscreen on some browsers
  }
}

// Auto-enter fullscreen on first user interaction when running as PWA
if (isStandalone()) {
  const enterOnce = () => {
    enterKioskMode();
    document.removeEventListener('click', enterOnce);
    document.removeEventListener('touchstart', enterOnce);
  };
  document.addEventListener('click', enterOnce, { once: true });
  document.addEventListener('touchstart', enterOnce, { once: true });
}

// Also expose for manual trigger (e.g., from a "Go fullscreen" button)
(window as any).__aegisEnterKiosk = enterKioskMode;
